import { useworkspaceAuthMeV2Query } from '@/types/generated/graphql';
import { useAllMembersList } from '@modules/conversations/features/all-members-conversation/hooks/use-all-members-list';
import { ALL_MEMBERS_ROUTE } from '@modules/conversations/features/all-members-conversation/all-members-conversation.constants';
import { MentionCandidate } from '@modules/conversations/conversation.types';
import { cacheOnly } from '@shared/constants';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

export function useMentionCandidates() {
  const { pathname } = useLocation();
  const { memberId = '' } = useParams();
  const isGroupRoom = pathname.startsWith(ALL_MEMBERS_ROUTE);

  const { members, loading } = useAllMembersList();
  const { data: workspaceAuthMe } = useworkspaceAuthMeV2Query(cacheOnly);
  const selfId = workspaceAuthMe?.workspaceAuthMeV2?.data?.currentUserWorkspace?.id;

  const everyone = useMemo(
    () =>
      members.map<MentionCandidate>((member) => ({
        id: Number(member?.id),
        name: member?.user?.name ?? '',
        role: member?.roles?.[0]?.label,
        profilePicture: member?.user?.profilePicture?.url,
        isOnline: !!member?.userOnlineManagement?.isActive,
        isSelf: Number(member?.id) === Number(selfId),
      })),
    [members, selfId]
  );

  const candidates = useMemo(() => {
    if (isGroupRoom) return everyone;

    const roomMemberIds = [Number(memberId), Number(selfId)];
    return everyone.filter((candidate) => roomMemberIds.includes(candidate.id));
  }, [everyone, selfId, isGroupRoom, memberId]);

  const labels = useMemo(() => {
    const byId = new Map<number, MentionCandidate>();
    for (const member of everyone) byId.set(member.id, member);
    return byId;
  }, [everyone]);

  return { candidates, labels, selfId: Number(selfId) || 0, canMentionAll: isGroupRoom, loading };
}
